import React, { useState, useEffect } from 'react';
import EditProfileForm from '../common/EditProfileForm';
import { storage } from '../../services/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useAuth } from '../../contexts/AuthContext';
import LocationPicker from '../common/LocationPicker';
import EditableFoodMenuAccordion from './EditableFoodMenuAccordion';

const ProviderEditForm = ({ profile, onSave, onCancel }) => {
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    type: '',
    licensed: false,
    ageGroups: [],
    ageGroupCapacities: {
      'Infant/Toddler': 0,
      'Preschool': 0,
      'School-Age': 0
    },
    languages: [],
    hoursOfOperation: {
      monday: { open: '', close: '' },
      tuesday: { open: '', close: '' },
      wednesday: { open: '', close: '' },
      thursday: { open: '', close: '' },
      friday: { open: '', close: '' },
      saturday: { open: '', close: '' },
      sunday: { open: '', close: '' }
    },
    fees: {
      hourly: '',
      daily: '',
      monthly: ''
    },
    programs: [],
    activities: [],
    screenTime: false,
    screenTimePolicy: '',
    foodMenu: '',
    certifications: [],
    email: '',
    address: '',
    displayName: '',
    phone: '',
    location: null
  });

  const [photos, setPhotos] = useState(profile?.photos || []);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const defaultFoodMenu = [
    {
      week: 1,
      days: [
        { day: "Monday", morning: "", lunch: "", afternoon: "" },
        { day: "Tuesday", morning: "", lunch: "", afternoon: "" },
        { day: "Wednesday", morning: "", lunch: "", afternoon: "" },
        { day: "Thursday", morning: "", lunch: "", afternoon: "" },
        { day: "Friday", morning: "", lunch: "", afternoon: "" }
      ]
    },
    {
      week: 2,
      days: [
        { day: "Monday", morning: "", lunch: "", afternoon: "" },
        { day: "Tuesday", morning: "", lunch: "", afternoon: "" },
        { day: "Wednesday", morning: "", lunch: "", afternoon: "" },
        { day: "Thursday", morning: "", lunch: "", afternoon: "" },
        { day: "Friday", morning: "", lunch: "", afternoon: "" }
      ]
    },
    {
      week: 3,
      days: [
        { day: "Monday", morning: "", lunch: "", afternoon: "" },
        { day: "Tuesday", morning: "", lunch: "", afternoon: "" },
        { day: "Wednesday", morning: "", lunch: "", afternoon: "" },
        { day: "Thursday", morning: "", lunch: "", afternoon: "" },
        { day: "Friday", morning: "", lunch: "", afternoon: "" }
      ]
    },
    {
      week: 4,
      days: [
        { day: "Monday", morning: "", lunch: "", afternoon: "" },
        { day: "Tuesday", morning: "", lunch: "", afternoon: "" },
        { day: "Wednesday", morning: "", lunch: "", afternoon: "" },
        { day: "Thursday", morning: "", lunch: "", afternoon: "" },
        { day: "Friday", morning: "", lunch: "", afternoon: "" }
      ]
    }
  ];

  useEffect(() => {
    if (profile) {
      setFormData(prev => ({
        ...prev,
        ...profile,
        foodMenu:
          profile.foodMenu && Array.isArray(profile.foodMenu) && profile.foodMenu.length > 0
            ? profile.foodMenu
            : defaultFoodMenu
      }));
    }
  }, [profile]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleArrayInputChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked 
        ? [...prev[name], value]
        : prev[name].filter(item => item !== value)
    }));
  };

  const handleHoursChange = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      hoursOfOperation: {
        ...prev.hoursOfOperation,
        [day]: {
          ...prev.hoursOfOperation[day],
          [field]: value
        }
      }
    }));
  };

  const handleFeesChange = (type, value) => {
    setFormData(prev => ({
      ...prev,
      fees: {
        ...prev.fees,
        [type]: value
      }
    }));
  };

  const handleCapacityChange = (group, value) => {
    setFormData(prev => ({
      ...prev,
      ageGroupCapacities: {
        ...prev.ageGroupCapacities,
        [group]: value
      }
    }));
  };

  const handleCertificationsChange = (index, value) => {
    setFormData(prev => {
      const updated = [...prev.certifications];
      updated[index] = value;
      return { ...prev, certifications: updated };
    });
  };

  const addCertification = () => {
    setFormData(prev => ({
      ...prev,
      certifications: [...prev.certifications, '']
    }));
  };

  const removeCertification = (index) => {
    setFormData(prev => {
      const updated = [...prev.certifications];
      updated.splice(index, 1);
      return { ...prev, certifications: updated };
    });
  };

  // Handle file selection
  const handlePhotoChange = async (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > 10) {
      setUploadError('You can upload a maximum of 10 images.');
      return;
    }
    setUploadError('');
    setUploading(true);
    const newPhotos = [];
    for (const file of files) {
      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        setUploadError('Only JPG and PNG images are allowed.');
        setUploading(false);
        return;
      }
      const providerUid = currentUser?.uid || profile?.id || 'temp';
      const storageRef = ref(storage, `provider_photos/${providerUid}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      newPhotos.push(url);
    }
    setPhotos(prev => [...prev, ...newPhotos].slice(0, 10));
    setUploading(false);
  };

  // Handle drag and drop
  const handleDrop = (e) => {
    e.preventDefault();
    handlePhotoChange({ target: { files: e.dataTransfer.files } });
  };
  const handleDragOver = (e) => e.preventDefault();

  // Remove photo
  const handleRemovePhoto = async (url) => {
    setPhotos(prev => prev.filter(p => p !== url));
    // Optionally delete from storage
    try {
      const photoRef = ref(storage, url);
      await deleteObject(photoRef);
    } catch (err) {}
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...formData, photos });
  };

  const foodMenu = Array.isArray(formData.foodMenu)
    ? formData.foodMenu
    : formData.foodMenu
      ? [formData.foodMenu]
      : [];

  return (
    <form className="edit-profile-form" onSubmit={handleSubmit}>
      <div className="form-section">
        <h2>Display Name</h2>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="displayName">Daycare Name or Licensee Name</label>
            <input
              type="text"
              id="displayName"
              name="displayName"
              value={formData.displayName || ''}
              onChange={handleInputChange}
              placeholder="Enter preferred name"
              required
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h2>Provider Details</h2>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="type">Provider Type</label>
            <select
              id="type"
              name="type"
              value={formData.type}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Type</option>
              <option value="center">Daycare Center</option>
              <option value="home">Daycare Home</option>
            </select>
          </div>
          <div className="form-group">
            <div className="checkbox-item">
              <input
                type="checkbox"
                id="licensed"
                name="licensed"
                checked={formData.licensed}
                onChange={handleInputChange}
              />
              <label htmlFor="licensed">Licensed Provider</label>
            </div>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h2>Age Groups & Capacity</h2>
        <div className="checkbox-grid">
          {['Infant/Toddler', 'Preschool', 'School-Age'].map(group => (
            <div key={group} className="checkbox-capacity-row">
              <input
                type="checkbox"
                id={`age-${group}`}
                name="ageGroups"
                value={group}
                checked={formData.ageGroups.includes(group)}
                onChange={handleArrayInputChange}
              />
              <label htmlFor={`age-${group}`}>{group === 'Infant/Toddler' ? <>Infant/<br />Toddler</> : group}</label>
              <input
                type="number"
                min="0"
                className="capacity-input"
                value={formData.ageGroupCapacities[group] !== undefined ? formData.ageGroupCapacities[group] : ''}
                onChange={e => handleCapacityChange(group, e.target.value)}
                placeholder="Max capacity"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="form-section">
        <h2>Languages</h2>
        <div className="checkbox-grid">
          {['English', 'Spanish', 'French', 'Mandarin', 'Hindi', 'Punjabi'].map(lang => (
            <div key={lang} className="checkbox-item">
              <input
                type="checkbox"
                id={`lang-${lang}`}
                name="languages"
                value={lang}
                checked={formData.languages.includes(lang)}
                onChange={handleArrayInputChange}
              />
              <label htmlFor={`lang-${lang}`}>{lang}</label>
            </div>
          ))}
        </div>
      </div>

      <div className="form-section">
        <h2>Hours of Operation</h2>
        <div className="hours-grid-vertical">
          {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => {
            const key = day.toLowerCase();
            return (
              <div key={day} className="day-hours-vertical">
                <div className="day-row-flex">
                  <div className="day-label">{day}</div>
                  <div className="time-row">
                    <label htmlFor={`${key}-open`}>Open</label>
                    <input
                      type="time"
                      id={`${key}-open`}
                      value={formData.hoursOfOperation[key].open}
                      onChange={e => handleHoursChange(key, 'open', e.target.value)}
                    />
                    <label htmlFor={`${key}-close`}>Close</label>
                    <input
                      type="time"
                      id={`${key}-close`}
                      value={formData.hoursOfOperation[key].close}
                      onChange={e => handleHoursChange(key, 'close', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="form-section">
        <h2>Fees</h2>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="hourly-fee">Hourly Rate ($)</label>
            <input
              type="number"
              id="hourly-fee"
              value={formData.fees.hourly}
              onChange={e => handleFeesChange('hourly', e.target.value)}
              min="0"
              step="0.01"
            />
          </div>
          <div className="form-group">
            <label htmlFor="daily-fee">Daily Rate ($)</label>
            <input
              type="number"
              id="daily-fee"
              value={formData.fees.daily}
              onChange={e => handleFeesChange('daily', e.target.value)}
              min="0"
              step="0.01"
            />
          </div>
          <div className="form-group">
            <label htmlFor="monthly-fee">Monthly Rate ($)</label>
            <input
              type="number"
              id="monthly-fee"
              value={formData.fees.monthly}
              onChange={e => handleFeesChange('monthly', e.target.value)}
              min="0"
              step="0.01"
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h2>Programs</h2>
        <div className="checkbox-grid">
          {['Full Day', 'Half Day', 'Before School', 'After School', 'Summer Camp', 'Weekend Care'].map(program => (
            <div key={program} className="checkbox-item">
              <input
                type="checkbox"
                id={`program-${program}`}
                name="programs"
                value={program}
                checked={formData.programs.includes(program)}
                onChange={handleArrayInputChange}
              />
              <label htmlFor={`program-${program}`}>{program}</label>
            </div>
          ))}
        </div>
      </div>

      <div className="form-section">
        <h2>Activities</h2>
        <div className="checkbox-grid">
          {['Arts & Crafts', 'Music', 'Sports', 'Outdoor Play', 'Reading', 'STEM Activities', 'Language Learning'].map(activity => (
            <div key={activity} className="checkbox-item">
              <input
                type="checkbox"
                id={`activity-${activity}`}
                name="activities"
                value={activity}
                checked={formData.activities.includes(activity)}
                onChange={handleArrayInputChange}
              />
              <label htmlFor={`activity-${activity}`}>{activity}</label>
            </div>
          ))}
        </div>
      </div>

      <div className="form-section">
        <h2>Screen Time Policy</h2>
        <textarea
          id="screenTimePolicy"
          name="screenTimePolicy"
          value={formData.screenTimePolicy || ''}
          onChange={handleInputChange}
          placeholder="Describe your screen time policy"
        />
      </div>

      <div className="form-section">
        <h2>Food Menu</h2>
        <EditableFoodMenuAccordion
          value={foodMenu}
          onChange={foodMenu => setFormData(prev => ({ ...prev, foodMenu }))}
        />
      </div>

      <div className="form-section">
        <h2>Certifications & Education</h2>
        {formData.certifications.map((cert, idx) => (
          <div key={idx} className="certification-row">
            <input
              type="text"
              value={cert}
              onChange={e => handleCertificationsChange(idx, e.target.value)}
              placeholder="Certification or education detail"
            />
            <button type="button" className="remove-cert-btn" onClick={() => removeCertification(idx)}>Remove</button>
          </div>
        ))}
        <button type="button" className="add-cert-btn" onClick={addCertification}>Add Certification</button>
      </div>

      <div className="form-section">
        <h2>Photo Gallery</h2>
        <div
          className="photo-upload-dropzone"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <input
            type="file"
            accept="image/jpeg,image/png"
            multiple
            onChange={handlePhotoChange}
            disabled={photos.length >= 10 || uploading}
            style={{ display: 'none' }}
            id="photo-upload-input"
          />
          <label htmlFor="photo-upload-input" className="photo-upload-label">
            {uploading ? 'Uploading...' : 'Drag & drop or click to select up to 10 images (JPG/PNG)'}
          </label>
        </div>
        {uploadError && <div className="error-message">{uploadError}</div>}
        <div className="photo-thumbnails-collage">
          {photos.map((url, idx) => (
            <div key={url} className="photo-thumb">
              <img src={url} alt={`Provider photo ${idx + 1}`} />
              <button type="button" className="remove-photo-btn" onClick={() => handleRemovePhoto(url)}>×</button>
            </div>
          ))}
        </div>
      </div>

      <div className="form-section">
        <h2>Contact & Location</h2>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email || ''}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone || ''}
              onChange={handleInputChange}
              placeholder="e.g. (555) 123-4567"
            />
          </div>
        </div>
        
        <div className="form-section">
          <h3>Location</h3>
          <LocationPicker
            initialLocation={formData.location}
            onLocationChange={(location) => {
              setFormData(prev => ({
                ...prev,
                location
              }));
            }}
            address={formData.address}
            onAddressChange={(address) => {
              setFormData(prev => ({
                ...prev,
                address
              }));
            }}
          />
        </div>
      </div>

      <div className="form-actions">
        <button type="submit" className="save-button">Save Changes</button>
        <button type="button" className="cancel-button" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
};

export default ProviderEditForm; 